import { Image, Skeleton } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { type BenchmarkMosaicProps, BenchmarkMosaicSize, type BenchmarkMosaicUrl } from './types';

const MOSAIC_CELLS = 4;

const gridClasses = cva({
  base: 'grid grid-cols-2 bg-white',
  variants: {
    size: {
      card: 'gap-1',
      thumbnail: 'h-20 w-20 shrink-0 gap-px overflow-hidden rounded-xs',
    },
  },
});

const cellClasses = cva({
  base: 'relative overflow-hidden bg-secondary',
  variants: {
    size: {
      card: 'aspect-[5/2] w-full',
      thumbnail: 'h-full w-full',
    },
  },
});

interface MosaicCellProps {
  url?: BenchmarkMosaicUrl;
  size: BenchmarkMosaicSize;
}

/**
 * One cell of the mosaic. A preview that fails to load gives way to its fallback, and with none left the cell reads as
 * empty, like a cell with no preview, rather than as a broken-image tile. A resize can be missing for good (it only
 * exists once the benchmark has been scored), so a URL that failed is not retried.
 */
function MosaicCell({ url, size }: MosaicCellProps) {
  // Remember every url that failed here, not just that one did: a new preview in this cell gets its own
  // attempt, while one that already failed stays hidden if it comes back.
  const [failedUrls, setFailedUrls] = React.useState<ReadonlySet<string>>(() => new Set());
  const candidates = typeof url === 'string' ? [url] : [url?.url, url?.fallbackUrl];
  const src = candidates.find((candidate) => candidate && !failedUrls.has(candidate));

  return (
    <div className={cellClasses({ size })}>
      {src ? (
        <Image
          key={src}
          src={src}
          name=""
          shape="unset"
          contain={false}
          retry={false}
          onError={() => setFailedUrls((previous) => new Set(previous).add(src))}
          className="h-full w-full"
        />
      ) : null}
    </div>
  );
}

/**
 * A 2×2 mosaic of a custom benchmark's images. Decorative: the benchmark name
 * is read from the surrounding text, so the mosaic is hidden from assistive tech.
 */
function BenchmarkMosaic(props: BenchmarkMosaicProps) {
  const { name, urls = [], isLoading = false, size = BenchmarkMosaicSize.Card, className } = props;

  return (
    <div className={cx(gridClasses({ size }), className)} aria-hidden>
      {Array.from({ length: MOSAIC_CELLS }, (_, index) => {
        const key = `${name}-mosaic-${index}`;

        if (isLoading) {
          return <Skeleton key={key} variant="square" className={cellClasses({ size })} />;
        }

        return <MosaicCell key={key} url={urls[index]} size={size} />;
      })}
    </div>
  );
}

export { BenchmarkMosaic };
